/**
 * Booking Step Manager
 * 
 * Container component that manages step rendering and transitions.
 * Supports animated transitions between steps using Framer Motion.
 * 
 * @module components/BookingStepManager
 */

import { type ReactNode, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Props for BookingStepManager
 */
export interface BookingStepManagerProps {
  /** Array of step components (one per step) */
  children: ReactNode[];
  /** Current active step index (0-based) */
  currentStep: number;
  /** Optional CSS class name */
  className?: string;
  /** Enable animations (default: true) */
  animated?: boolean;
}

/**
 * Animation variants for step transitions
 */
const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction < 0 ? 300 : -300,
    opacity: 0,
  }),
};

/**
 * Booking Step Manager
 * 
 * Renders the current step component with smooth animations.
 * Uses Framer Motion for slide transitions between steps.
 * 
 * @example
 * ```tsx
 * <BookingStepManager currentStep={currentStep}>
 *   <BookingTimeStep />
 *   <PaymentDetailsStep />
 *   <FinishAccountStep />
 * </BookingStepManager>
 * ```
 */
export function BookingStepManager({
  children,
  currentStep,
  className = '',
  animated = true,
}: BookingStepManagerProps) {
  // Track previous step in state to determine animation direction
  const [direction, setDirection] = useState(0);
  const [prevStep, setPrevStep] = useState(currentStep);
  
  // Ensure currentStep is within valid bounds
  const childrenArray = Array.isArray(children) ? children : [children];
  const safeStepIndex = Math.max(0, Math.min(currentStep, childrenArray.length - 1));
  
  // Update direction when currentStep changes
  if (currentStep !== prevStep) {
    setDirection(currentStep > prevStep ? 1 : -1);
    setPrevStep(currentStep);
  }

  // If animations disabled, just show current step
  if (!animated) {
    return (
      <div className={`booking-step-container mt-16 ${className}`}>
        <div className="booking-step" data-step={safeStepIndex}>
          {childrenArray[safeStepIndex]}
        </div>
      </div>
    );
  }

  // Animated version
  return (
    <div className={`booking-step-container relative overflow-hidden mt-16 ${className}`}>
      <AnimatePresence initial={false} mode="wait" custom={direction}>
        <motion.div
          key={safeStepIndex}
          custom={direction}
          variants={stepVariants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{
            x: { type: 'spring', stiffness: 300, damping: 30 },
            opacity: { duration: 0.2 },
          }}
          className="booking-step"
          data-step={safeStepIndex}
        >
          {childrenArray[safeStepIndex]}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
